// dependencies
import { GenerateProcess } from "redux-process";

import { DisplaySuccess } from "../../../actions/notifications.actions";
// actions
import { StateChange } from "../../../actions/page.actions";

// selectors
import { selectRequestingNotifications } from "./selectors";

// config
import config from "./config";
// ui
import ui from "./ui";

/**
 * Initialize the view by fetching notifications and then initilizing the UI
 * after the requests have finished.
 */
export const init = () => async (dispatch) => {
	dispatch(StateChange(config.pageName, [], ui));

	// do all network io
	await Promise.all([
		dispatch(GenerateProcess("getWeeklyDigestSettingsTemplate")),
		dispatch(GenerateProcess("getAdminWeeklyDigestSettingsTemplate")),
		dispatch(GenerateProcess("getAsapAndDeferredNotificationSettingsTemplate")),
	]).catch(() => null);

	dispatch(StateChange(config.pageName, ["initialized"], true));
};

/**
 * Teardown the view.
 */
export const teardown = () => () => {};

/**
 * This will send notification settings, but not for the timing requests.
 */
export const sendNotificationSettingsPatch = (notification, type, processName) => async (dispatch, getState) => {
	// create a new list of outgoing requests
	const requestingList = selectRequestingNotifications(getState()).setIn([notification], type);

	// update state
	dispatch(StateChange(config.pageName, ["awaitingRequests"], requestingList));

	// create the request payload
	const payload = {
		notificationSettings: [{ name: notification, type }],
	};

	// send request
	try {
		await dispatch(GenerateProcess(processName, { payload }));
	} catch (ermahgerd) {
		// pass
	}

	// remove item from requesting list
	const newRequestingList = selectRequestingNotifications(getState()).delete(notification);

	// update state
	dispatch(StateChange(config.pageName, ["awaitingRequests"], newRequestingList));
};

/**
 * Sends a PATCH to update the notification state of an asap and deferred
 * notification template. It will also update a list of requests that are
 * ongoing in the page state.
 * @param {string} notification - the notification identifier
 * @param {string} type - the value to set for that notification
 */
export const sendAsapAndDeferredPatch = (notification, type) => (dispatch) => {
	dispatch(sendNotificationSettingsPatch(notification, type, "setAsapAndDeferredNotificationSettingsTemplate"));
};

/**
 * Sends a PATCH to update the notification state of a weekly digest
 * notification template. It will also update a list of requests that are
 * ongoing in the page state.
 * @param {string} notification - the notification identifier
 * @param {string} type - the value to set for that notification
 */
export const sendDigestPatch = (notification, type) => (dispatch) => {
	dispatch(sendNotificationSettingsPatch(notification, type, "setWeeklyDigestSettingsTemplate"));
};

/**
 * Sends a PATCH to update the notification state of an admin weekly digest
 * notification template. It will also update a list of requests that are
 * ongoing in the page state.
 * @param {string} notification - the notification identifier
 * @param {string} type - the value to set for that notification
 */
export const sendAdminDigestPatch = (notification, type) => (dispatch) => {
	dispatch(sendNotificationSettingsPatch(notification, type, "setAdminWeeklyDigestSettingsTemplate"));
};

/**
 * Sends a PATCH with the notification times for the deferred notification
 * template.
 * @param {object[]} times - an array of objects with the time, no mre than two
 *
 * @example
 *
 *  sendDeferredTimingPatch([{ hour: 16 }, { hour: 12 }])
 */
export const sendDeferredTimingPatch = (times) => async (dispatch) => {
	// update state
	dispatch(StateChange(config.pageName, ["awaitingTimingRequest"], true));

	// create the request payload
	const payload = {
		frequencySettings: times,
	};

	// send request
	try {
		const request = await dispatch(
			GenerateProcess("setAsapAndDeferredNotificationSettingsTemplate", {
				payload,
			})
		);

		if (request.succeeded) {
			dispatch(DisplaySuccess("Updated daily notification settings"));
		}
	} catch (ermahgerd) {
		// pass
	}

	// update state
	dispatch(StateChange(config.pageName, ["awaitingTimingRequest"], false));
};

/**
 * Updates the timing configuration of the weekly digest template.
 * @param {string} dayOfWeek - day of the week
 * @param {number} hour - 0-23 hour
 */
export const sendWeeklyDigestTimingPatch = (dayOfWeek, hour) => async (dispatch) => {
	// update state
	dispatch(StateChange(config.pageName, ["awaitingDigestTimingRequest"], true));

	// create the request payload
	const payload = {
		deliverySettings: {
			hour,
			dayOfWeek,
		},
	};

	// send request
	try {
		const request = await dispatch(GenerateProcess("setWeeklyDigestSettingsTemplate", { payload }));

		if (request.succeeded) {
			dispatch(DisplaySuccess("Updated weekly digest notification settings"));
		}
	} catch (ermahgerd) {
		// pass
	}

	// update state
	dispatch(StateChange(config.pageName, ["awaitingDigestTimingRequest"], false));
};

/**
 * Updates the timing configuration of the weekly digest template.
 * @param {string} dayOfWeek - day of the week
 * @param {number} hour - 0-23 hour
 */
export const sendWeeklyAdminDigestTimingPatch = (dayOfWeek, hour) => async (dispatch) => {
	// update state
	dispatch(StateChange(config.pageName, ["awaitingDigestTimingRequest"], true));

	// create the request payload
	const payload = {
		deliverySettings: {
			hour,
			dayOfWeek,
		},
	};

	// send request
	try {
		const request = await dispatch(
			GenerateProcess("setAdminWeeklyDigestSettingsTemplate", {
				payload,
			})
		);

		if (request.succeeded) {
			dispatch(DisplaySuccess("Updated weekly admin digest notification settings"));
		}
	} catch (ermahgerd) {
		// pass
	}

	// update state
	dispatch(StateChange(config.pageName, ["awaitingDigestTimingRequest"], false));
};

/**
 * Updates the active notification section.
 * @param {string} sectionName - name of the new active section
 */
export const updateActiveSection = (sectionName) => (dispatch) => {
	dispatch(StateChange(config.pageName, ["activeSection"], sectionName));
};
